import { Fragment } from 'react';
import { Link } from 'react-router';
import { FolderPlus, Plus } from 'lucide-react';
import { Button } from '../ui/Button';
import { Menu, MenuItem } from '../ui/Menu';
import { itemCountLabel, matchCountLabel } from '../ui/counts';
import { useRoomDialogs } from '../rooms/RoomDialogs';
import { SearchButton } from '../search/SearchButton';
import { sectionName } from '../db/sections';
import type { Room, Section } from '../db/schema';

/**
 * The room's name, what's in it, and the ways to change either.
 *
 * Inside a section the title becomes the whole path — "Closet - Winter" —
 * with everything above the last part a link back to it. That reads as a
 * sentence and works as navigation, which is one element doing both jobs
 * rather than a breadcrumb bolted under a heading.
 */
export function BoardHeader({
  room,
  path,
  count,
  matching,
  dragging,
  holdingId,
  arrived,
  onAddItem,
  onAddSection,
  onEditSection,
  onDeleteSection,
}: {
  room: Room;
  /** Outermost first, ending with the section you're in. Empty at the root. */
  path: readonly Section[];
  count: number;
  /** How many of the room a search is showing. Undefined when none is on. */
  matching: number | undefined;
  /** Something is being dragged: the path above here can take it. */
  dragging: boolean;
  /** The crumb held over long enough that releasing would move it there. */
  holdingId: string | undefined;
  /** Increments each time a drag springs into this container. */
  arrived: number;
  onAddItem: () => void;
  onAddSection: () => void;
  onEditSection: () => void;
  onDeleteSection: () => void;
}) {
  const dialogs = useRoomDialogs();
  const here = path.at(-1);

  return (
    <header className="board__header">
      <div>
        <div className="heading-row">
          {/* Keyed on the arrival count so the blink replays: a CSS animation
              only runs when the element is new to it. */}
          <h1 className="display board__title" key={arrived}>
            <Crumb
              to={`/room/${room.id}`}
              last={path.length === 0}
              // Every crumb above where you are is somewhere the dragged thing
              // could go — which is the only way back out of a section.
              dropId={path.length === 0 ? undefined : room.id}
              dragging={dragging}
              holding={holdingId === room.id}
              blink={arrived > 0 && path.length === 0}
            >
              {room.name}
            </Crumb>

            {path.map((section, index) => {
              const last = index === path.length - 1;
              return (
                <Fragment key={section.id}>
                  <span className="board__crumb-dash"> - </span>
                  <Crumb
                    to={`/room/${room.id}/s/${section.id}`}
                    last={last}
                    // Not the one you're in: it's already there.
                    dropId={last ? undefined : section.id}
                    dragging={dragging}
                    holding={holdingId === section.id}
                    blink={arrived > 0 && last}
                  >
                    {sectionName(section)}
                  </Crumb>
                </Fragment>
              );
            })}
          </h1>

          {/* Scoped to this room, which is what the heading beside it names.
              The search itself reaches every section inside. */}
          <SearchButton scope="room" label={`Search ${room.name}`} />
        </div>

        <p className="board__count">
          {matching === undefined ? itemCountLabel(count) : matchCountLabel(matching, count)}
        </p>
      </div>

      <div className="board__tools">
        <Button variant="quiet" onClick={onAddSection}>
          <FolderPlus size={18} aria-hidden />
          Add Section
        </Button>

        <Button onClick={onAddItem}>
          <Plus size={18} aria-hidden />
          Add Item
        </Button>

        <Menu label={here === undefined ? `Actions for ${room.name}` : `Actions for ${sectionName(here)}`}>
          {(close) =>
            here === undefined ? (
              <>
                <MenuItem
                  onSelect={() => {
                    close();
                    dialogs.openEdit(room);
                  }}
                >
                  Edit Room
                </MenuItem>
                <MenuItem
                  onSelect={() => {
                    close();
                    dialogs.openDelete(room);
                  }}
                >
                  Delete Room
                </MenuItem>
              </>
            ) : (
              <>
                <MenuItem
                  onSelect={() => {
                    close();
                    onEditSection();
                  }}
                >
                  Edit Section
                </MenuItem>
                <MenuItem
                  onSelect={() => {
                    close();
                    onDeleteSection();
                  }}
                >
                  Delete Section
                </MenuItem>
              </>
            )
          }
        </Menu>
      </div>
    </header>
  );
}

function Crumb({
  to,
  last,
  dropId,
  dragging,
  holding,
  blink = false,
  children,
}: {
  to: string;
  last: boolean;
  /** Set where a dragged thing can be dropped to move it here. */
  dropId: string | undefined;
  dragging: boolean;
  holding: boolean;
  /** Just sprung into: says so, since nothing else moved to announce it. */
  blink?: boolean;
  children: string;
}) {
  if (last) {
    return (
      <span className={`board__crumb board__crumb--here${blink ? ' board__crumb--blink' : ''}`}>
        {children}
      </span>
    );
  }

  const classes = [
    'board__crumb',
    dropId !== undefined && dragging ? 'board__crumb--target' : '',
    holding ? 'board__crumb--holding' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Link to={to} className={classes} data-drop-id={dropId}>
      {children}
    </Link>
  );
}
